/**
 * Hook useServiceQueueRealtime
 *
 * Subscription Realtime ÚNICA para o Kanban da Fila de Atendimento.
 * Substitui as 2 subscriptions por coluna (conversations + transfers)
 * × 5 colunas = 10 canais globais, por UM ÚNICO canal por aba/tenant.
 *
 * CHANGELOG:
 *   v1.3.0 - 2026-06-26 — REMOVIDA a escuta de `conversation_transfers`
 *            no mesmo canal. Essa tabela NÃO está publicada em
 *            `supabase_realtime`, e ao incluí-la o canal inteiro pode
 *            ficar incompleto/silencioso — sintoma reportado: cards
 *            da Fila só atualizavam ao pressionar F5, enquanto o
 *            Kanban Conversa (que assina apenas `conversations`)
 *            funcionava normalmente. Também passamos a logar o status
 *            do canal (SUBSCRIBED/CHANNEL_ERROR/TIMED_OUT/CLOSED)
 *            para diagnóstico futuro.
 *   v1.2.0 - 2026-06-26 — Patch otimista varre TODAS as colunas em
 *            cache (`service-queue-column`) à procura do card pelo id.
 *   v1.1.0 - 2026-06-26 — Log de TODO evento antes do filtro; filtro
 *            de tenant tolerante na fase inicial; patch defensivo só
 *            sobrescreve campos presentes no payload.
 *   v1.0.0 - 2026-06-26 — Versão inicial inspirada em
 *            useKanbanConversationRealtime v1.1.0 (1 canal, patch
 *            otimista com reorder-ao-topo e coalescing 1500ms).
 */
import { useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTenantInboxIds } from '@/hooks/useTenantInboxIds';
import type { QueueItem, QueueSituation } from './useServiceQueue';

const logger = createLogger('ServiceQueueRealtime');

// Janela de coalescing — agrupa rajadas em um único ciclo de refetch
const DEBOUNCE_MS = 1500;

// Tempo mínimo em background para invalidar tudo ao voltar ao foco.
// Evita refetch completo em microtrocas de aba (ex.: clicar no DevTools).
const VISIBILITY_MIN_HIDDEN_MS = 30 * 1000;


/**
 * Deriva a "situation" da fila a partir dos campos da tabela conversations.
 * NÃO cobre o estado pending_transfer (depende de conversation_transfers);
 * eventos de transfer disparam refetch debounced separadamente.
 */
function deriveSituation(row: {
  status?: string | null;
  assigned_to?: string | null;
  department_id?: string | null;
} | undefined): QueueSituation | null {
  if (!row) return null;
  if (row.status === 'closed') return 'closed';
  if (row.assigned_to) return 'in_service';
  if (row.department_id) return 'waiting_agent';
  return 'waiting_bot';
}

/**
 * Hook montado UMA VEZ no QueueKanbanBoard.
 *
 * @param enabled - permite desligar a subscription (default true)
 */
export function useServiceQueueRealtime(enabled: boolean = true) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const inboxIdsRef = useTenantInboxIds();

  // Situations que precisam de refetch (acumulam até o debounce disparar)
  const pendingSituationsRef = useRef<Set<QueueSituation>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statsDirtyRef = useRef(false);

  useEffect(() => {
    if (!currentTenant?.id || !enabled) return;

    /**
     * (v1.2.0) Patch otimista que varre TODAS as colunas em cache
     * (`service-queue-column`, qualquer situation/filtros) à procura do
     * card pelo id. Aplica patch in-place ou reordena ao topo da
     * primeira página se a mensagem ficou mais recente.
     *
     * Vantagem: independe da "situation" derivada localmente — o card
     * é atualizado na coluna em que ele REALMENTE está, inclusive
     * `pending_transfer` (que não é derivável só pelos campos da
     * tabela conversations).
     *
     * Retorna `true` se localizou o card em pelo menos uma query.
     */
    const patchItemAcrossColumns = (
      conversationId: string,
      patch: Partial<QueueItem>,
    ): boolean => {
      let patched = false;

      const queries = queryClient.getQueriesData<InfiniteData<QueueItem[]>>({
        queryKey: ['service-queue-column'],
      });

      queries.forEach(([key, data]) => {
        if (!data?.pages?.length) return;

        let foundItem: QueueItem | null = null;
        let foundPageIdx = -1;
        for (let i = 0; i < data.pages.length; i++) {
          const idx = data.pages[i].findIndex((c) => c.id === conversationId);
          if (idx >= 0) {
            foundItem = data.pages[i][idx];
            foundPageIdx = i;
            break;
          }
        }

        if (!foundItem) return;

        patched = true;
        const merged: QueueItem = { ...foundItem, ...patch };

        const oldTs = foundItem.last_message_at ? Date.parse(foundItem.last_message_at) : 0;
        const newTs = merged.last_message_at ? Date.parse(merged.last_message_at) : 0;
        const shouldReorder = newTs > oldTs;

        if (!shouldReorder) {
          const newPages = data.pages.map((page) =>
            page.map((c) => (c.id === conversationId ? merged : c)),
          );
          queryClient.setQueryData(key, { ...data, pages: newPages });
          return;
        }

        // Reordena: remove da posição original e insere no topo da página 0
        const newPages = data.pages.map((page, i) => {
          const filtered = i === foundPageIdx ? page.filter((c) => c.id !== conversationId) : page;
          return i === 0 ? [merged, ...filtered] : filtered;
        });
        queryClient.setQueryData(key, { ...data, pages: newPages });
      });

      return patched;
    };

    /** Dispara invalidação debounced — 1 vez por coluna + stats. */
    const scheduleFlush = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        const sits = Array.from(pendingSituationsRef.current);
        pendingSituationsRef.current.clear();

        if (sits.length > 0) {
          logger.debug(`Flush debounced: invalidando ${sits.length} coluna(s)`, sits);
          sits.forEach((sit) => {
            queryClient.invalidateQueries({
              queryKey: ['service-queue-column', sit],
            });
          });
        }

        if (statsDirtyRef.current) {
          statsDirtyRef.current = false;
          queryClient.invalidateQueries({
            queryKey: ['service-queue-stats'],
          });
        }
      }, DEBOUNCE_MS);
    };

    /** Marca uma situation como dirty e agenda flush. */
    const markSituationDirty = (situation: QueueSituation | null, statsToo = false) => {
      if (situation) pendingSituationsRef.current.add(situation);
      if (statsToo) statsDirtyRef.current = true;
      scheduleFlush();
    };

    logger.debug('Configurando Realtime unificado da Fila de Atendimento');

    const channel = supabase
      .channel(`service-queue-board-${currentTenant.id}`)
      // ----- conversations -----
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        (payload) => {
          const newRow = payload.new as any;
          const oldRow = payload.old as any;
          const inboxId = newRow?.inbox_id || oldRow?.inbox_id;

          logger.debug('Evento conversations recebido', {
            eventType: payload.eventType,
            inbox_id: inboxId,
            conv_id: newRow?.id || oldRow?.id,
          });

          const knownInboxes = inboxIdsRef.current;
          if (knownInboxes && knownInboxes.size > 0 && inboxId && !knownInboxes.has(inboxId)) {
            return; // Evento de outro tenant
          }

          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

          const eventType = payload.eventType;

          if (eventType === 'INSERT') {
            markSituationDirty(deriveSituation(newRow), true);
            return;
          }
          if (eventType === 'DELETE') {
            markSituationDirty(deriveSituation(oldRow), true);
            return;
          }

          // UPDATE
          const oldSit = deriveSituation(oldRow);
          const newSit = deriveSituation(newRow);
          const situationChanged = oldSit !== newSit;

          if (situationChanged) {
            // Movimentação estrutural entre colunas → refetch das duas + stats
            markSituationDirty(oldSit, true);
            markSituationDirty(newSit, true);
            return;
          }

          // Mesma situation: PATCH OTIMISTA varrendo todas as colunas
          // (cobre o caso pending_transfer, em que o card está numa
          // coluna que não é derivável só pela tabela conversations).
          if (newRow?.id) {
            const patch: Partial<QueueItem> = {};
            if ('last_message_at' in newRow) patch.last_message_at = newRow.last_message_at ?? null;
            if ('last_message_preview' in newRow) patch.last_message_preview = newRow.last_message_preview ?? null;
            if ('unread_count' in newRow && typeof newRow.unread_count === 'number') {
              patch.unread_count = newRow.unread_count;
            }
            if ('status' in newRow && newRow.status) {
              patch.status = newRow.status as QueueItem['status'];
            }

            const patched = Object.keys(patch).length > 0
              ? patchItemAcrossColumns(newRow.id, patch)
              : false;

            if (patched) {
              logger.debug('Patch otimista aplicado em card visível', { conv_id: newRow.id });
            } else if (newSit) {
              // Card não está em nenhuma coluna em cache → refetch da
              // coluna derivada para garantir consistência visual.
              markSituationDirty(newSit, false);
            }
          }
        },
      )

      // ----- conversation_transfers -----
      // REMOVIDO em v1.3.0: a tabela `conversation_transfers` NÃO está
      // publicada em `supabase_realtime`. Mantê-la no mesmo canal junto
      // de `conversations` fazia a assinatura ficar incompleta e os
      // eventos de `conversations` (mensagens novas) não chegarem ao
      // callback — sintoma: cards só atualizavam com F5. Movimentos de
      // transferência continuam refletindo via UPDATE de
      // assigned_to/department_id em `conversations` (que JÁ está
      // publicada) e via fechamento do modal de chat.
      .subscribe((status) => {
        logger.debug(`Realtime Fila status: ${status}`);
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          logger.warn(`Realtime Fila em estado problemático: ${status}`);
        }
      });

    // Visibilidade: só invalida se a aba ficou oculta por mais de
    // VISIBILITY_MIN_HIDDEN_MS — evita refetch a cada microtroca de foco
    // (ex.: alt+tab rápido, abrir DevTools), que disparava timeouts (57014)
    // na RPC pesada de stats.
    let hiddenAt: number | null = null;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      // visible
      const elapsed = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;
      if (elapsed >= VISIBILITY_MIN_HIDDEN_MS) {
        logger.debug(`Aba voltou após ${Math.round(elapsed / 1000)}s — invalidando colunas e stats`);
        queryClient.invalidateQueries({ queryKey: ['service-queue-column'] });
        queryClient.invalidateQueries({ queryKey: ['service-queue-stats'] });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);


    return () => {
      logger.debug('Removendo Realtime unificado da Fila de Atendimento');
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, enabled, queryClient, inboxIdsRef]);
}
